import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  Lightbulb,
  TrendingUp,
  Users,
  Brain,
  Target,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

/**
 * Functional Assessment page — restyled to match the SearchJob.ai landing
 * theme: cream background, forest-green primary, gradient heading accents
 * (primary → emerald → teal), eyebrow pills, rounded-3xl cards, pill CTAs,
 * and dark-ink bands for the stats + final CTA.
 *
 * All copy, data arrays and links are preserved from the original page;
 * only the presentation was brought in line with the landing page.
 */

const INK = 'hsl(164 33% 7%)'; // brand dark-ink surface (Stats / CTA bands)

export default function FunctionalAssessment() {
  const navigate = useNavigate();

  useSEO({
    title:
      'Functional & Skills Assessments — Role-Based Candidate Evaluation | SearchJob.ai',
    description:
      'Evaluate technical and domain skills with customised, role-based functional assessments — standardised, unbiased and data-backed.',
    canonical: 'https://searchjob.ai/hirewithus/functional-assessment',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  const assessmentTypes = [
    { icon: Lightbulb, title: 'Behavior Test', desc: 'Evaluate workplace scenarios, conflict resolution, and decision-making under pressure.' },
    { icon: TrendingUp, title: 'Personality Test', desc: 'Assess traits, work style preferences, cultural fit, and leadership potential.' },
    { icon: Users, title: 'Communication Test', desc: 'Test verbal skills, presentation abilities, and interpersonal effectiveness.' },
  ];

  const capabilities = [
    { title: 'Behavioral', items: ['Conflict resolution', 'Decision making', 'Team collaboration'] },
    { title: 'Personality', items: ['Work style fit', 'Cultural alignment', 'Leadership potential'] },
    { title: 'Communication', items: ['Verbal skills', 'Active listening', 'Interpersonal effectiveness'] },
  ];

  const benefits = [
    { num: '1', title: 'Scientifically Validated Instruments', desc: 'Evidence-based assessment tools with proven reliability and validity for accurate candidate evaluation' },
    { num: '2', title: 'Comprehensive Competency Mapping', desc: 'Multi-dimensional evaluation that maps candidate competencies to organizational requirements' },
    { num: '3', title: 'Predictive Success Modeling', desc: 'AI-powered algorithms that predict candidate success based on functional assessment performance' },
  ];

  const stats = [
    { number: '75%', text: 'Assessment Accuracy' },
    { number: '4X', text: 'Better Cultural Fit' },
    { number: '50%', text: 'Reduction in Turnover' },
  ];

  const methodologies = [
    { title: 'Behavioral Assessment', desc: 'Uses situational judgment tests (SJTs) and behavioral event interviews (BEIs) to evaluate how candidates handle real workplace challenges.' },
    { title: 'Personality Assessment', desc: 'Implements validated personality inventories and work style questionnaires to assess traits relevant to job performance and team dynamics.' },
    { title: 'Communication Assessment', desc: 'Combines video presentations, interactive scenarios, and verbal exercises to evaluate communication effectiveness across different contexts.' },
  ];

  const features = [
    { icon: Brain, title: 'Behavioral Pattern Recognition', desc: 'AI-powered analysis of candidate responses to identify behavioral patterns and predict workplace performance' },
    { icon: TrendingUp, title: 'Personality Trait Mapping', desc: 'Machine learning models that map personality traits to job requirements and team compatibility' },
    { icon: Users, title: 'Communication Style Analysis', desc: 'Advanced NLP capabilities for understanding communication patterns and effectiveness' },
    { icon: Target, title: 'Predictive Success Modeling', desc: 'Comprehensive algorithms that predict candidate success based on functional assessment performance' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          {/* Gradient mesh + grid, matching the landing hero */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              AI-Powered Functional Assessment Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-6">
              Evaluate Core Competencies
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                with Precision
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              Assess the three core competencies that determine workplace
              success — behavioral patterns, personality traits, and
              communication skills — with AI-powered precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/hirewithus/login')}
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
              >
                Start Assessment
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/hirewithus/contact')}
                className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </section>

        {/* ═════════════ OVERVIEW ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Overview</Eyebrow>
              <SectionHeading plain="Assess the competencies that" accent="drive workplace success." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Our AI-powered functional assessment platform specializes in evaluating the three core competencies
                that determine workplace success: behavioral patterns, personality traits, and communication skills.
                Built with advanced psychological assessment algorithms, it provides comprehensive insights into
                candidate potential and organizational fit.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                From situational judgment scenarios to personality profiling and communication effectiveness analysis,
                our platform ensures that you hire candidates who not only have the right skills but also the right
                mindset and interpersonal abilities for long-term success.
              </p>
              <Button
                size="lg"
                onClick={() => navigate('/hirewithus/login')}
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
              >
                Start Assessment
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </div>
            <ImageCard
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
              alt="AI-powered functional assessment interface"
            />
          </div>
        </Section>

        {/* ═════════════ THREE CORE ASSESSMENT TYPES ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Assessment types</Eyebrow>
            <SectionHeading center plain="Three core" accent="assessment types." />
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {assessmentTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.title}
                  className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300 text-center"
                >
                  <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25 mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {type.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {type.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ CORE CAPABILITIES ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Core capabilities</Eyebrow>
            <SectionHeading center plain="Essential tools for" accent="workplace success." />
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {capabilities.map((capability) => (
              <div
                key={capability.title}
                className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
              >
                <h3 className="text-base font-bold text-foreground mb-4 leading-snug">
                  {capability.title}
                </h3>
                <ul className="space-y-3">
                  {capability.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
                      </span>
                      <span className="text-base text-foreground/85 leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* ═════════════ WHY CHOOSE OUR PLATFORM ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1516116216624-53e697fedbea?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2128&q=80"
              alt="AI technology innovation"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Why choose us</Eyebrow>
              <SectionHeading plain="Why choose" accent="our platform?" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Traditional functional assessments often rely on subjective evaluations and outdated methodologies.
                Our AI-powered solution addresses these challenges with scientifically validated instruments,
                intelligent analysis, and data-driven insights.
              </p>
              <div className="space-y-5">
                {benefits.map((benefit) => (
                  <div key={benefit.num} className="flex items-start gap-4">
                    <div className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-800 text-primary-foreground flex items-center justify-center font-black text-lg shadow-lg shadow-primary/25">
                      {benefit.num}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark ink) ═════════════ */}
        <section className="relative py-14 lg:py-20 overflow-hidden" style={{ backgroundColor: INK }}>
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {stats.map((stat) => (
                <div key={stat.text}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{stat.text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ ASSESSMENT METHODOLOGIES ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Methodologies</Eyebrow>
            <SectionHeading center plain="Assessment" accent="methodologies." />
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our platform employs cutting-edge assessment methodologies that combine psychological science
              with artificial intelligence to deliver accurate, reliable, and actionable insights.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {methodologies.map((methodology) => (
              <div
                key={methodology.title}
                className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
              >
                <h3 className="text-base font-bold text-foreground mb-3 leading-snug">
                  {methodology.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {methodology.desc}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ═════════════ ADVANCED AI CAPABILITIES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="AI-powered assessment technology"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Advanced AI</Eyebrow>
              <SectionHeading plain="Advanced" accent="AI capabilities." />
              <div className="space-y-4">
                {features.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={feature.title}
                      className="bg-card border border-border/70 rounded-3xl p-5 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
                    >
                      <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug flex items-center gap-2.5">
                        <span className="shrink-0 w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-primary" />
                        </span>
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        {/* ═════════════ FINAL CTA (dark ink) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl text-center"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                  Ready to Transform Your
                  <br />
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    Hiring Process?
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed mb-8">
                  Experience the future of functional assessment with AI-powered precision, scientifically validated instruments, and comprehensive insights that help you build stronger, more cohesive teams.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                  >
                    Contact Us
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus/login')}
                    className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                  >
                    Hire With Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Small shared bits — mirror the landing page's building blocks    */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false }) {
  return (
    <section
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[540px] mx-auto h-auto rounded-3xl border border-border shadow-2xl ring-1 ring-foreground/5"
      />
    </div>
  );
}
